import { useState, useEffect, useContext, useCallback } from 'react';
import { Page, ButtonGroup, Button, BlockStack } from '@shopify/polaris';
import { SessionContext } from '#client/contexts/SessionContext.jsx';
import { useLabelPrintersQuery } from '#client/components/features/LabelPrinting/hooks/useLabelPrintersQuery';
import { useZebraPrinterSelection } from '#client/components/features/LabelPrinting/hooks/useZebraPrinterSelection';
import { catalogsForLocation, defaultPriceSelectionFor, priceListIdFor } from '#client/components/features/LabelPrinting/utils/productLabels';
import BySkuTab from './components/BySkuTab.jsx';
import ByStyleTab from './components/ByStyleTab.jsx';

export default function PrintLabels() {
    const { currentUser } = useContext(SessionContext);
    const [selectedTab, setSelectedTab] = useState(0);

    // Print settings are shared, so they survive a tab switch. Search results and
    // quantities stay local to each tab and reset with it.
    const [printMode, setPrintMode] = useState('pdf');
    const [skuType, setSkuType] = useState('sku');
    const [priceSelection, setPriceSelection] = useState('');
    const [catalogs, setCatalogs] = useState([]);

    const enableZebraCloudPrinting = true;
    const labelPrintersQuery = useLabelPrintersQuery({ enabled: enableZebraCloudPrinting });

    const zebraSelection = useZebraPrinterSelection({
        enabled: enableZebraCloudPrinting,
        locations: labelPrintersQuery.locations,
        defaultLocationGid: currentUser?.activeStore,
    });

    const locationGid = zebraSelection.selectedLocationGid;
    const priceListId = priceListIdFor(priceSelection);

    // Unchanged from the previous single-tab page: a catalog that no longer covers the
    // location is cleared, and a location with exactly one applicable catalog gets it.
    useEffect(() => {
        if (catalogs.length === 0 && !locationGid) return;

        setPriceSelection((current) => {
            const stillApplies = current && catalogsForLocation(catalogs, locationGid).some((catalog) => catalog.priceListId === current);
            return stillApplies ? current : defaultPriceSelectionFor(catalogs, locationGid);
        });
    }, [catalogs, locationGid]);

    const handleTabChange = useCallback((index) => setSelectedTab(index), []);

    const shared = {
        locationGid,
        printMode,
        onPrintModeChange: setPrintMode,
        skuType,
        onSkuTypeChange: setSkuType,
        priceSelection,
        onPriceSelectionChange: setPriceSelection,
        priceListId,
        catalogs,
        onCatalogsChange: setCatalogs,
        zebraSelection,
        labelPrintersQuery,
        enableZebraCloudPrinting,
    };

    return (
        <Page title="Print Labels" fullWidth>
            <BlockStack gap="400">
                <ButtonGroup variant="segmented">
                    <Button variant={selectedTab === 0 ? 'primary' : undefined} pressed={selectedTab === 0} onClick={() => handleTabChange(0)}>
                        By SKU / UPC
                    </Button>
                    <Button variant={selectedTab === 1 ? 'primary' : undefined} pressed={selectedTab === 1} onClick={() => handleTabChange(1)}>
                        By Style
                    </Button>
                </ButtonGroup>
                {selectedTab === 0 ? <BySkuTab {...shared} /> : <ByStyleTab {...shared} />}
            </BlockStack>
        </Page>
    );
}
